import { pickT } from "@repo/shared";
import type { AssetClassForecastHorizon } from "@repo/shared/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./asset-class-forecast-form.i18n";
import { TimeRangeField } from "./time-range-field";
import type { ComponentFormProps } from "./types";

export type AssetClassForecastConfig = {
  title?: string;
  description?: string;
  /** QL-414 — absent on a component saved before the setting existed: 10Y. */
  timeRange?: AssetClassForecastHorizon;
  showChart: boolean;
  showTable: boolean;
};

type AssetClassForecastFormProps = ComponentFormProps<AssetClassForecastConfig>;

export function AssetClassForecastForm({ config, onChange }: AssetClassForecastFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="acf-pdf-title">{t.title}</Label>
        <Input
          id="acf-pdf-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="acf-pdf-description">{t.description}</Label>
        <Textarea
          id="acf-pdf-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <TimeRangeField
        id="acf-pdf-time-range"
        value={config.timeRange}
        onChange={(timeRange) => onChange({ ...config, timeRange })}
      />

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="acf-pdf-show-chart" className="text-base">
            {t.showChart}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showChartHelp}</p>
        </div>
        <Switch
          id="acf-pdf-show-chart"
          checked={config.showChart !== false}
          onCheckedChange={(checked) => onChange({ ...config, showChart: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="acf-pdf-show-table" className="text-base">
            {t.showTable}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showTableHelp}</p>
        </div>
        <Switch
          id="acf-pdf-show-table"
          checked={config.showTable || false}
          onCheckedChange={(checked) => onChange({ ...config, showTable: checked })}
        />
      </div>

      {!config.showChart && !config.showTable && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {t.validationError}
        </div>
      )}
    </div>
  );
}

export function validateAssetClassForecastConfig(
  config: AssetClassForecastConfig,
  lang = "en_GB",
): string | null {
  const t = pickT(T, lang);
  if (!config.showChart && !config.showTable) {
    return t.validationErrorLong;
  }
  return null;
}

export function getDefaultAssetClassForecastConfig(): AssetClassForecastConfig {
  return {
    title: "Asset Class Forecast",
    description: "How the asset class mix shifts over time.",
    timeRange: "10Y",
    showChart: true,
    showTable: false,
  };
}
